import type {
  FetcherClient,
  LodTreeNode,
  PointCloudBufferGeometry,
} from "@faro-lotv/lotv";

const REQUEST_WEIGHT = 0.01;

/**
 * Request one LOD node through an independent fetcher client, outside the rendering policy.
 * The caller owns the lease and must call `fetcher.releaseNode(node.id, client)` afterwards.
 *
 * @param client Fetcher client holding the lease.
 * @param node Node to load.
 * @param signal Cancellation; rejects with its reason.
 * @returns The loaded node geometry.
 */
export function loadLodNode(
  client: FetcherClient,
  node: LodTreeNode,
  signal: AbortSignal,
): Promise<PointCloudBufferGeometry> {
  return new Promise((resolve, reject) => {
    const { lodTreeFetcher: fetcher } = client;
    function cleanup(): void {
      ready.dispose();
      failed.dispose();
      signal.removeEventListener("abort", abort);
    }
    function abort(): void {
      cleanup();
      reject(signal.reason);
    }
    const ready = fetcher.nodeReady.on((event) => {
      if (event.nodeIdx !== node.id) return;
      cleanup();
      resolve(event.points);
    });
    const failed = fetcher.nodeFailed.on((event) => {
      if (event.nodeIdx !== node.id) return;
      cleanup();
      reject(new Error("Point cloud node unavailable"));
    });
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) {
      abort();
      return;
    }
    try {
      fetcher.requestNodes([{ id: node.id, weight: REQUEST_WEIGHT }], client);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}
